import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const Terms = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      color: theme.text,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px 20px',
      borderBottom: `1px solid ${theme.border}`,
      position: 'sticky',
      top: 0,
      backgroundColor: theme.bg,
      zIndex: 10,
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: theme.accent,
      cursor: 'pointer',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      margin: 0,
      color: theme.text,
    },
    content: {
      padding: '20px',
      maxWidth: '800px',
      margin: '0 auto',
      lineHeight: 1.6,
    },
    lastUpdated: {
      color: theme.textMuted,
      fontSize: '14px',
      marginBottom: '24px',
    },
    text: {
      color: theme.textSecondary,
      fontSize: '14px',
      marginBottom: '16px',
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: theme.text,
      marginTop: '32px',
      marginBottom: '16px',
      paddingBottom: '8px',
      borderBottom: `1px solid ${theme.border}`,
    },
    subTitle: {
      fontSize: '15px',
      fontWeight: '600',
      color: theme.text,
      marginTop: '20px',
      marginBottom: '12px',
    },
    bold: {
      color: theme.text,
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '8px',
    },
    list: {
      color: theme.textSecondary,
      fontSize: '14px',
      paddingLeft: '24px',
      marginBottom: '16px',
    },
    highlight: {
      color: '#f59e0b',
      fontSize: '14px',
      fontWeight: '600',
      padding: '12px 16px',
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      borderRadius: '8px',
      marginBottom: '16px',
    },
    warningBox: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
    },
    warningTitle: {
      color: '#ef4444',
      fontSize: '14px',
      fontWeight: '700',
      margin: 0,
      textAlign: 'center',
    },
    finalText: {
      color: theme.textMuted,
      fontSize: '13px',
      marginTop: '32px',
      paddingTop: '16px',
      borderTop: `1px solid ${theme.border}`,
      fontStyle: 'italic',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </button>
        <h1 style={styles.title}>Terms of Service</h1>
      </div>

      <div style={styles.content}>
        <p style={styles.lastUpdated}>Last Updated: January 11, 2025</p>

        <p style={styles.text}>
          Welcome to CardChase. Please read these Terms of Service ("Terms") carefully before using our mobile application.
        </p>
        <p style={styles.text}>
          By creating an account or using CardChase, you agree to these Terms. If you don't agree, please don't use our service.
        </p>

        <h2 style={styles.sectionTitle}>1. What CardChase Is (And Isn't)</h2>

        <p style={styles.bold}>CardChase IS:</p>
        <ul style={styles.list}>
          <li>A platform to discover, showcase, and organize your trading card collection</li>
          <li>A tool to connect with other collectors who may want to trade</li>
          <li>A directory of locations where you can meet for trades</li>
          <li>A scheduling tool to arrange in-person meetups</li>
        </ul>

        <p style={styles.bold}>CardChase IS NOT:</p>
        <ul style={styles.list}>
          <li>A guarantee that any trade will be fair, safe, or successful</li>
          <li>A verification service for card authenticity or condition</li>
          <li>A guarantee that any location is safe</li>
          <li>A party to any transaction between users</li>
          <li>An endorsement of any user, card listing, or location</li>
          <li>A verification of anyone's identity</li>
        </ul>

        <p style={styles.highlight}>We are a connector and communication tool. Nothing more.</p>

        <h2 style={styles.sectionTitle}>2. Who Can Use CardChase</h2>

        <h3 style={styles.subTitle}>Age Requirements</h3>
        <ul style={styles.list}>
          <li><strong>13 years or older:</strong> You can create your own account</li>
          <li><strong>Under 13 years old:</strong> You MUST use CardChase through a parent or guardian account</li>
        </ul>

        <h3 style={styles.subTitle}>Account Rules</h3>
        <ul style={styles.list}>
          <li>You can only have one account</li>
          <li>Your account information must be accurate</li>
          <li>You are responsible for keeping your login secure</li>
          <li>You are responsible for everything that happens on your account</li>
        </ul>

        <h2 style={styles.sectionTitle}>3. User Conduct - The Rules</h2>

        <h3 style={styles.subTitle}>What You CAN'T Post</h3>
        <ul style={styles.list}>
          <li>Sexually explicit or NSFW content (including card images)</li>
          <li>Violent, threatening, or graphic content</li>
          <li>Hateful content targeting any group</li>
          <li>Fraudulent or misleading card listings</li>
          <li>Counterfeit or replica cards presented as authentic</li>
          <li>Content that infringes on others' copyrights or trademarks</li>
          <li>Spam or unauthorized advertising</li>
          <li>Anyone's personal information without their consent</li>
        </ul>

        <h3 style={styles.subTitle}>What You CAN'T Do</h3>
        <ul style={styles.list}>
          <li>Engage in predatory behavior toward any user, especially minors</li>
          <li>Harass, stalk, bully, or intimidate other users</li>
          <li>Attempt to scam or defraud other users</li>
          <li>Create fake accounts or multiple accounts</li>
          <li>Manipulate ratings, reviews, or the platform</li>
          <li>Use CardChase for anything illegal</li>
          <li>Attempt to circumvent any safety features</li>
          <li>Impersonate others or misrepresent yourself</li>
        </ul>

        <p style={styles.highlight}>Violation of these rules may result in immediate account termination without warning.</p>

        <h2 style={styles.sectionTitle}>4. In-Person Meetups and Trades - IMPORTANT</h2>

        <div style={styles.warningBox}>
          <p style={styles.warningTitle}>READ THIS SECTION CAREFULLY. THIS IS CRITICAL.</p>
        </div>

        <h3 style={styles.subTitle}>You Meet At Your Own Risk</h3>
        <p style={styles.text}>
          When you arrange to meet another user through CardChase, you are doing so entirely at your own risk. CardChase is not involved in your meetup in any way. We do not:
        </p>
        <ul style={styles.list}>
          <li>Verify the identity of users</li>
          <li>Verify the authenticity or condition of cards</li>
          <li>Guarantee anyone's honesty or intentions</li>
          <li>Provide any security or supervision</li>
          <li>Guarantee that any trade will be completed</li>
        </ul>

        <h3 style={styles.subTitle}>About "Approved" Locations</h3>
        <p style={styles.text}>
          CardChase displays locations where trades can occur. <strong>Being listed on CardChase does NOT mean:</strong>
        </p>
        <ul style={styles.list}>
          <li>We have verified the location is safe</li>
          <li>We have inspected the premises</li>
          <li>We endorse the location</li>
          <li>We guarantee security at the location</li>
          <li>The location will protect you in any way</li>
        </ul>
        <p style={styles.text}>
          <strong>What "approved" actually means:</strong> The location has agreed to be listed and may pay for enhanced features. That's it. We have not visited, inspected, or verified anything about these locations.
        </p>

        <h3 style={styles.subTitle}>Our Recommendations (Not Requirements)</h3>
        <ul style={styles.list}>
          <li>Meet in public, well-lit areas during business hours</li>
          <li>Tell someone where you're going and when</li>
          <li>Bring a friend when possible</li>
          <li>Verify cards carefully before completing any trade</li>
          <li>Trust your instincts - if something feels wrong, leave</li>
          <li>Never share personal information like your home address</li>
        </ul>

        <h3 style={styles.subTitle}>CardChase Has No Liability For</h3>
        <ul style={styles.list}>
          <li>Physical harm, injury, or death during meetups</li>
          <li>Theft, robbery, assault, or any crime</li>
          <li>Failed, unfair, or disputed trades</li>
          <li>Counterfeit, damaged, or misrepresented cards</li>
          <li>Any incident at any location, whether listed or not</li>
          <li>Any action or inaction by any user</li>
          <li>Any action or inaction by any location or its staff</li>
        </ul>

        <p style={styles.highlight}>
          You agree that CardChase, its owners, employees, and affiliates have ZERO liability for anything that happens during or related to any meetup or trade arranged through our platform.
        </p>

        <h2 style={styles.sectionTitle}>5. Messaging and Communications</h2>
        <ul style={styles.list}>
          <li>Messages are sent directly to other users</li>
          <li>You agree not to send prohibited content (see Section 3)</li>
          <li>CardChase may review messages for safety and compliance</li>
          <li>You have no expectation of privacy in messages</li>
          <li>You consent to receive push notifications and system messages</li>
        </ul>

        <h2 style={styles.sectionTitle}>6. Your Content</h2>
        <p style={styles.text}>
          You retain ownership of content you post. By posting on CardChase, you grant us a worldwide, non-exclusive, royalty-free license to use, display, and distribute your content in connection with operating CardChase.
        </p>
        <p style={styles.text}>
          CardChase can remove any content at any time, for any reason, without notice.
        </p>

        <h2 style={styles.sectionTitle}>7. Intellectual Property</h2>
        <p style={styles.text}>
          Pokemon, trading card game names, card images, and related marks are trademarks of their respective owners. CardChase is not affiliated with, endorsed by, or sponsored by any trading card game company.
        </p>

        <h2 style={styles.sectionTitle}>8. Disclaimers</h2>
        <p style={styles.text}>
          CARDCHASE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR MEET YOUR EXPECTATIONS.
        </p>

        <h2 style={styles.sectionTitle}>9. Limitation of Liability</h2>
        <p style={styles.text}>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW: CardChase's total liability is limited to the amount you paid us in the past 12 months. We are not liable for indirect, incidental, special, consequential, or punitive damages.
        </p>

        <h2 style={styles.sectionTitle}>10. Indemnification</h2>
        <p style={styles.text}>
          You agree to defend and hold harmless CardChase from any claims arising from your use of CardChase, your content, your trades and meetups, or your violation of these Terms.
        </p>

        <h2 style={styles.sectionTitle}>11. Dispute Resolution</h2>
        <p style={styles.text}>
          These Terms are governed by California law. Disputes will be resolved through binding arbitration. You waive any right to participate in class actions.
        </p>

        <h2 style={styles.sectionTitle}>12. Account Termination</h2>
        <p style={styles.text}>
          CardChase may suspend or terminate your account at any time. Serious violations (predatory behavior, fraud, threats, harassment) result in immediate termination without warning.
        </p>

        <h2 style={styles.sectionTitle}>13. Changes to These Terms</h2>
        <p style={styles.text}>
          We may update these Terms. Continued use after changes means you accept the new Terms.
        </p>

        <h2 style={styles.sectionTitle}>14. Contact Us</h2>
        <p style={styles.text}>
          <strong>CardChase</strong><br />
          Email: tradersupport@cardchase.org<br />
          Address: Trainer Center, 4911 Warner Ave #210, Huntington Beach, CA 92649
        </p>

        <p style={styles.finalText}>
          By using CardChase, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
        </p>
      </div>
    </div>
  );
};

export default Terms;
